import type { Override, ReadModel } from '../../core/api/adapters';
import type { OptimizedTableDetail, TableDataCellForOptimizedTableDetail, TableSearchItem } from '../../core/api/schema';
import type {
  IAttachedTable,
  ICellSearchItem,
  HyperLink,
  IProjectTag,
  ITableIssue,
  ITableItem,
  TableId,
  WireTableIssue,
} from '../../project/models';
import type { SlimUser } from '../../user/models/user';
import { isDefined } from './is-defined.util';

export type ITableBackendResponse = Override<ReadModel<OptimizedTableDetail, TableId>, { issues: Record<string, WireTableIssue> }>;

export type IResDataCellItem = TableDataCellForOptimizedTableDetail;

export const convertSearchItem = (
  backSearchItem: TableSearchItem | null | undefined,
  cellSearchItem: IResDataCellItem['larisearch_item'],
  issues: ITableIssue[],
  tables: IAttachedTable[],
): ICellSearchItem | null => {
  if (!cellSearchItem || !backSearchItem) {
    return null;
  }
  const li = backSearchItem;
  const headings = cellSearchItem.headings ?? '';
  let head;
  if (li.sections && typeof headings === 'string' && headings in li.sections) {
    head = li.sections[headings];
  } else {
    head = {};
  }
  // Backend-to-frontend conversion: the produced item strips `sections` and resolves the
  // `attached_to_*` url references into domain objects (hence the ICellSearchItem return
  // type). The `as` bridges one remaining divergence: `updated_on` is set null here, and
  // `ISearchItem` types it `string | undefined`.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `updated_on` is set null here, per the comment above
  return {
    ...li,
    ...head,
    updated_on: li.is_update && li.timestamp ? li.timestamp.split(' ', 1)[0] : null,
    sections: undefined,
    attached_to_issue: li.attached_to_issue?.map(url => issues.find(i => i.url === url)).filter((i): i is ITableIssue => Boolean(i)),
    attached_to_table: li.attached_to_table?.map(url => tables.find(t => t.url === url)).filter((t): t is IAttachedTable => Boolean(t)),
  } as ICellSearchItem;
};

export const convertIssue = (issue: WireTableIssue, tags: Record<string, IProjectTag>, users: Record<string, SlimUser>): ITableIssue => ({
  ...issue,
  id: issue.id,
  url: issue.url,
  title: issue.title ?? '',
  tags: issue.tags?.map(t => tags[t]).filter((t): t is IProjectTag => t !== undefined) ?? [],
  assignee: issue.assignee ? users[issue.assignee] : undefined,
});

export const convertTable = (table: ITableBackendResponse): ITableItem => {
  const { rows, owners, datacells, larisearch_items, datacolumns, project, columns, external_destinations, ...rest } = table;
  // Wire→view boundary. Ids arrive branded from the spec (`x-brand`), so the map embeds
  // (`users`/`tags`/`tables`), `project`/`external_destinations` and the row/cell pks assign directly.
  const users: Record<string, SlimUser> = table.users;
  const tags: Record<string, IProjectTag> = table.tags;
  const tables: Record<string, IAttachedTable> = table.tables;
  const issues = Object.values(table.issues).map(i => convertIssue(i, tags, users));
  const tablesList = Object.values(tables);

  return {
    ...rest,
    project,
    columns,
    external_destinations,
    datacolumns: Object.values(datacolumns),
    owners: owners.map(o => users[o]).filter(isDefined),
    tables,
    users,
    rows: Object.values(rows).map(r => {
      const { datacells: backDataCells, ...restRow } = r;
      return {
        ...restRow,
        datacells: backDataCells
          .map(datacellURL => datacells[datacellURL])
          .filter(isDefined)
          .map(dc => {
            const { larisearch_item } = dc;

            const issue = dc.issue ? issues.find(i => i.url === dc.issue) : null;

            return {
              ...dc,
              users: dc.users?.map(u => users[u]).filter(isDefined) ?? [],
              tags: dc.tags?.map(t => tags[t]).filter(isDefined) ?? [],
              issue: issue ?? null,
              larisearch_item: larisearch_item?.id
                ? convertSearchItem(larisearch_items[larisearch_item.id], larisearch_item, issues, tablesList)
                : null,
              // Wire `HyperLink` is looser than the FE view (url/text optional,
              // `target` a raw string vs the FE `EnumLinkTarget`). Assert at this boundary like the
              // other resolved fields — the FE type is deliberately more precise. See REMAINING.md.
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the FE `HyperLink` is deliberately narrower than the wire type
              hyperlink: (dc.hyperlink ?? null) as HyperLink | null,
            };
          }),
      };
    }),
  };
};
